<template>
  <div id="backtester-stock-filters-tab">
    <v-container
      fluid
      grid-list-xl
      pa-0
    >
      <v-row>
        <!-- Restrict by market cap -->
        <v-col
          cols="12"
          xl="6"
        >
          <v-combobox
            v-model="backtesterCriteria.market_caps"
            :items="marketCapsItems"
            attach
            item-text="name"
            item-value="id"
            label="Restrict by market cap"
            menu-props="offsetY"
            multiple
            chips
            small-chips
            deletable-chips
            clearable
            dense
            hide-details
          />
        </v-col>

        <v-col v-show="$vuetify.breakpoint.xlOnly" />

        <!-- Limit to Certain Sectors -->
        <v-col
          cols="12"
          xl="6"
        >
          <helpers-section-sub-header class="mb-2">
            Restrict by Sector/Industry
          </helpers-section-sub-header>
          <v-treeview
            v-model="backtesterCriteria.industries"
            :items="sectors"
            open-on-click
            transition
            hoverable
            return-object
            selectable
            dense
          />
        </v-col>

        <!-- ETF Filter -->
        <v-col
          cols="12"
          class="mt-4"
        >
          <helpers-section-sub-header>ETF Filter</helpers-section-sub-header>
          <v-radio-group
            v-model="etfFilter"
            row
            hide-details
            class="mt-1"
          >
            <v-radio
              :value="0"
              label="Any"
              color="primary"
            />
            <v-radio
              :value="1"
              label="ETF Holdings only"
              color="primary"
            />
            <v-radio
              :value="2"
              label="Exclude ETFs"
              color="primary"
            />
          </v-radio-group>
        </v-col>

        <!-- ADR Filter -->
        <v-col cols="12">
          <helpers-section-sub-header class="d-inline-flex align-center">
            <v-chip
              v-if="!accessAllowed({ requires_pro_subscription: true })"
              color="accent white--text"
              class="v-chip--x-small font-weight-bold ma-0 mr-2"
            >
              PRO
            </v-chip>
            ADR Filter
          </helpers-section-sub-header>
          <v-radio-group
            v-model="adrFilter"
            :disabled="!accessAllowed({ requires_pro_subscription: true })"
            row
            hide-details
            class="mt-1"
          >
            <v-radio
              :value="0"
              label="Any"
              color="primary"
            />
            <v-radio
              :value="1"
              label="Foreign ADR only"
              color="primary"
            />
            <v-radio
              :value="2"
              label="Exclude ADRs"
              color="primary"
            />
          </v-radio-group>
        </v-col>

        <!-- Do not include stocks whose price exceeds -->
        <v-col
          cols="12"
          md="6"
          xl="3"
        >
          <helpers-section-sub-header>Do not include stocks whose price exceeds:</helpers-section-sub-header>
          <v-text-field
            v-model.number="backtesterCriteria.exclusion_more_than"
            type="number"
            placeholder="Enter price"
            prefix="$"
            hide-details
          />
        </v-col>

        <v-col v-show="$vuetify.breakpoint.lgOnly" />

        <!-- Do not include stocks whose price is less than -->
        <v-col
          cols="12"
          md="6"
          xl="3"
        >
          <helpers-section-sub-header>Do not include stocks whose price is less than:</helpers-section-sub-header>
          <v-text-field
            v-model.number="backtesterCriteria.exclusion_less_than"
            type="number"
            placeholder="Enter price"
            prefix="$"
            hide-details
          />
        </v-col>

        <v-col v-show="$vuetify.breakpoint.lgAndUp" />

        <!-- Exclude specific tickers -->
        <v-col
          cols="12"
          lg="6"
        >
          <helpers-section-sub-header>Exclude specific tickers:</helpers-section-sub-header>
          <v-text-field
            v-model="backtesterCriteria.exclusion_tickers"
            placeholder="Comma-separated list of tickers"
          />
        </v-col>
        <!-- stocks vs s and p perf-->
        <v-col
          cols="12"
          mb-6
        >
          <helpers-section-sub-header>
            Filter Stocks Based on Perf vs S&amp;P
            <sup class="accent--text ml-1">BETA</sup>
          </helpers-section-sub-header>

          <v-row>
            <v-col cols="12">
              Only include stocks that are either up or down vs the S&amp;P 500 in the past [1,3,6,9,12] months
            </v-col>

            <v-col
              cols="12"
              pt-0
            >
              <v-switch
                v-model="backtesterCriteria.stocks_vs_sp.enabled"
                color="primary"
                label="Enable "
                hint="Enable filtering stocks by performance vs S&P"
                persistent-hint
                class="mt-0"
              />
            </v-col>

            <!-- Stop loss/gain exit strategies: Inputs -->
            <template v-if="backtesterCriteria.stocks_vs_sp.enabled">
              <v-col
                cols="12"
                lg="6"
              >
                <v-select
                  v-model="backtesterCriteria.stocks_vs_sp.months"
                  :items="stocks_vs_sp_months"
                  label="Months"
                  hint="Months"
                  persistent-hint
                  single-line
                />
              </v-col>
              <v-col
                cols="12"
                lg="6"
              >
                <v-select
                  v-model="backtesterCriteria.stocks_vs_sp.up_or_down"
                  :items="stocks_vs_sp_up_down"
                  label="Up or Down"
                  hint="Up or down vs S&amp;P"
                  persistent-hint
                  single-line
                />
              </v-col>
              <v-col
                cols="12"
                lg="6"
              >
                <v-text-field
                  v-model="backtesterCriteria.stocks_vs_sp.percent_threshold"
                  label="Percent Threshold"
                  hint="Include stocks up or down __ Percent vs the S&P"
                  placeholder="Percent Threshold"
                />
              </v-col>
              <v-col
                cols="12"
                lg="6"
              >
                <v-text-field
                  v-model="backtesterCriteria.stocks_vs_sp.plus_minus"
                  hint="Optional-Include only stocks that are up plus or minus __ Percentage Points over the S&P"
                  label="Hurdle Rate: Plus or Minus"
                  placeholder="Plus or Minus"
                />
              </v-col>
            </template>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
// Modules
import { mapFields } from 'vuex-map-fields'
import { mapState } from 'vuex'

// Mixins
import AccessMixin from '@/mixins/access'

export default {
  mixins: [AccessMixin],

  data: () => ({
    // etfFilter: 0,
    // adrFilter: 0,
    marketCapsItems: [
      { name: 'Over $200 billion', id: 1 },
      { name: '$100 billiion - $200 billion', id: 2 },
      { name: '$50 billion - $100 billion', id: 3 },
      { name: '$20 billion - $50 billion', id: 4 },
      { name: '$10 billion - $20 billion', id: 5 },
      { name: '$2 billion - $10 billion', id: 6 },
      { name: '$250 million - $2 billion', id: 7 },
      { name: 'Below $250 million', id: 8 }
    ],
    stocks_vs_sp_months: [1, 3, 6, 9, 12],
    stocks_vs_sp_up_down: ['up', 'down']
  }),

  computed: {
    ...mapState('ww', ['sectors']),
    ...mapFields({
      backtesterCriteria: 'app.backtesterCriteria',
      etfFilter: 'app.etfFilter',
      adrFilter: 'app.adrFilter'
    }),

    // When we reset Backtester Options we need to manually reset
    // the etfFilter & adrFilter since they are proxies
    // and aren't directly modeled after any criteria props
    // If this returns TRUE we set the ETF and ADR radio groups to 'Any'
    onOptionsReset () {
      return (
        !this.backtesterCriteria.etf_only &&
        !this.backtesterCriteria.exclude_etf &&
        !this.backtesterCriteria.adr_only &&
        !this.backtesterCriteria.exclude_adr
      )
    }
  },

  watch: {
    onOptionsReset (bool) {
      if (bool) {
        this.etfFilter = 0
        this.adrFilter = 0
      }
    },

    etfFilter (val) {
      // Any
      if (val === 0) {
        this.backtesterCriteria.etf_only = false
        this.backtesterCriteria.exclude_etf = false
      }
      // ETF Only
      if (val === 1) {
        this.backtesterCriteria.etf_only = true
        this.backtesterCriteria.exclude_etf = false
        this.backtesterCriteria.adr_only = false
      }
      // Exlude ETFs
      if (val === 2) {
        this.backtesterCriteria.etf_only = false
        this.backtesterCriteria.exclude_etf = true
      }
    },

    adrFilter (val) {
      // Any
      if (val === 0) {
        this.backtesterCriteria.adr_only = false
        this.backtesterCriteria.exclude_adr = false
      }
      // ADR Only
      if (val === 1) {
        this.backtesterCriteria.etf_only = false
        this.backtesterCriteria.adr_only = true
        this.backtesterCriteria.exclude_adr = false
      }
      // Exlude ADRs
      if (val === 2) {
        this.backtesterCriteria.adr_only = false
        this.backtesterCriteria.exclude_adr = true
      }
    }
  }
}
</script>
